import { TableChart, TableRows } from '@mui/icons-material';
import CompareIcon from '@mui/icons-material/Compare';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import
    {
        Box,
        Chip,
        IconButton,
        Paper,
        Stack,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableRow,
        Typography,
        useTheme,
    } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EEntityType, ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import getEmployees from '@src/APIs/graphQl/Employee/getEmployees';
import MvpEmployeeCard, { type MvpEmployeeData } from '@src/App/Components/Core/Cards/MvpEmployeeCard/MvpEmployeeCard';
import { Container } from '@src/App/Components/Core/Layouts/LayoutElements/Container/Container';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import { EmployeePicture } from '@src/App/Components/General/EmployeePicture/EmployeePicture';
import EmployeeOverlay from '@src/App/Components/Overlays/EmployeeOverlay/EmployeeOverlay';
import type { IDataTableField, IDataTableSettings } from '@src/App/Components/Tables/DataTable/DataTable';
import { getPrimaryRoleTitle } from '@src/App/Helper/roleHelper';
import EntitiesPage, {
    EViewType,
    type IEntitiesPageAction,
} from '@src/App/Pages/EntityPages/Generic/EntitiesPage/EntitiesPage';
import type { IViewType } from '@src/App/Pages/EntityPages/Generic/EntitiesPage/ViewTypeMenu';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import { textContainsLowerCase } from '@src/Objects/Helper';
import type { AppState } from '@store/store';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface TeamMember
{
    id: number;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    pictureId: string | null;
    availability: number;
    description: string;
    skills: { title: string; name?: string }[];
    roles: { title: string }[];
    role: string;
    certificates: { title: string }[];
    projects: { title: string; active?: boolean }[];
    orgUnit: string;
    status: string;
    statusColor: 'success' | 'warning' | 'error';
    title: string;
    version: number;
    type: EEntityType.EMPLOYEE;
    link: string;
}

export default function MyTeamPage()
{
    const theme = useTheme();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const [selected, setSelected] = useState<number[]>([]);
    const [activeRole, setActiveRole] = useState('manager');

    const orgUnits = useSelector((state: AppState) => state.OUs);
    const currentUser = useSelector((state: AppState) => state.employee);
    const employees = useSelector((state: AppState) => state.employees);
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);

    // Get team member IDs from OrgUnits where current user is leader
    const teamMemberIds = useMemo(() =>
    {
        const myOrgUnits = orgUnits.filter((ou) => ou.leader === currentUser.id);
        const memberIds = myOrgUnits
            .flatMap((ou) => ou.employees || [])
            .map((emp: number | { id: number }) => (typeof emp === 'number' ? emp : emp.id))
            .filter((empId) => empId !== currentUser.id);
        console.log('👥 Team Member IDs:', memberIds, 'from', myOrgUnits.length, 'org units');
        return memberIds;
    }, [orgUnits, currentUser.id]);

    // Load all employees and org units once
    useEffect(() =>
    {
        getEmployees().catch((error: Error) =>
        {
            console.error('Failed to load employees:', error);
        });
    }, []);

    // Log org units for debugging
    useEffect(() =>
    {
        console.log('🏢 OrgUnits in state:', orgUnits.map(ou => ({
            id: ou.id,
            title: ou.title,
            leader: ou.leader,
            employeesCount: ou.employees?.length || 0,
            employees: ou.employees,
        })));
    }, [orgUnits]);

    // Build team members from loaded employee data
    const teamMembers: TeamMember[] = useMemo(() =>
    {
        const myOrgUnits = orgUnits.filter((ou) => ou.leader === currentUser.id);

        return employees
            .filter((emp) => teamMemberIds.includes(emp.id))
            .filter((emp) => emp.id !== currentUser.id)
            .map((emp) =>
            {
                const nameParts = (emp.title ?? '').trim().split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
                const availability =
                    typeof emp.availability === 'number' ? emp.availability : emp.availability?.availability || 0;
                const roles = emp.roles || [];

                return {
                    id: emp.id,
                    name: emp.title,
                    firstName,
                    lastName,
                    email: emp.email,
                    pictureId: emp.pictureId,
                    availability,
                    description: emp.description || '',
                    skills: emp.skills || [],
                    roles,
                    role: getPrimaryRoleTitle(roles) || 'Rolle nicht definiert',
                    certificates: emp.certificates || [],
                    projects: emp.projects || [],
                    orgUnit: myOrgUnits.find((ou) => ou.employees.some((e: number | { id: number }) => (typeof e === 'number' ? e : e.id) === emp.id))?.title || 'Unbekannt',
                    status:
                        availability >= 60 ? 'Verfügbar' : availability >= 30 ? 'Teilweise verfügbar' : 'Ausgelastet',
                    statusColor: availability >= 60 ? 'success' : availability >= 30 ? 'warning' : 'error',
                    title: emp.title,
                    version: 1,
                    type: EEntityType.EMPLOYEE,
                    link: `/${ELinks.PEOPLE_EMPLOYEES}/${emp.id}`,
                };
            });
    }, [employees, teamMemberIds, orgUnits, currentUser.id]);

    const getAvailabilityColor = (availability: number) =>
    {
        return availability >= 60
            ? theme.palette.success.main
            : availability >= 30
                ? theme.palette.warning.main
                : theme.palette.error.main;
    };

    const getAvailabilityGradient = (availability: number) =>
    {
        if (availability >= 60)
        {
            return `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.secondary.main} 100%)`;
        } else if (availability >= 30)
        {
            return `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.secondary.main} 100%)`;
        } else
        {
            return `linear-gradient(90deg, ${theme.palette.error.main} 0%, ${theme.palette.secondary.main} 100%)`;
        }
    };

    const renderTeamStatsChips = () => (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1, mb: 2 }}>
            <Chip label={`${teamMembers.length} Team Mitglieder`} color="primary" variant="outlined" size="small" />
            <Chip
                label={`Ø ${Math.round(teamMembers.reduce((sum, m) => sum + m.availability, 0) / teamMembers.length || 0)}% Verfügbarkeit`}
                color="primary"
                variant="outlined"
                size="small"
            />
            <Chip
                label={`${teamMembers.filter((m) => m.availability >= 60).length} verfügbar`}
                color="primary"
                variant="outlined"
                size="small"
            />
        </Stack>
    );

    const types: IViewType<any>[] = [
        {
            id: EViewType.CARD,
            title: 'Cards',
            icon: <TableChart fontSize="small" />,
            renderContainer: (allEntities: any[], innerEntries: any[]) => (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                        gap: 3,
                        p: 2,
                    }}
                >
                    {innerEntries.map((member: any) =>
                    {
                        const fullEmployee = employees.find((emp) => emp.id === member.id);
                        const focusSuperPower = fullEmployee?.skills
                            ?.filter((skill) => skill.level >= 3)
                            ?.sort((a, b) => b.level - a.level)?.[0]; // First superpower is the focus power

                        const employeeData: MvpEmployeeData = {
                            id: member.id,
                            firstName: member.firstName,
                            lastName: member.lastName,
                            pictureId: member.pictureId,
                            role: member.role,
                            orgUnit: member.orgUnit,
                            email: member.email,
                            availability: member.availability,
                            superPower: focusSuperPower?.title,
                        };

                        const handleManagerAction = (action: string, employeeId: number) =>
                        {
                            switch (action)
                            {
                                case 'view-profile':
                                    navigate(`/${ELinks.PEOPLE_EMPLOYEES}/${employeeId}`);
                                    break;
                                case 'send-message':
                                    console.log('Send message to employee', employeeId);
                                    break;
                                case 'schedule-meeting':
                                    navigate(`/${ELinks.EMPLOYEE_TALKS}`);
                                    break;
                                default:
                                    console.log('Unknown action:', action);
                            }
                        };

                        return (
                            <MvpEmployeeCard
                                key={member.id}
                                employee={employeeData}
                                context="employees"
                                userRole="manager"
                                onAction={handleManagerAction}
                            />
                        );
                    })}
                </Box>
            ),
        },
        {
            id: EViewType.TABLE,
            title: 'Tabelle',
            icon: <TableRows fontSize="small" />,
            minWidth: 375,
            renderContainer: (
                allEntities: TeamMember[],
                innerEntries: TeamMember[],
                width: number,
                selectedItems: number[],
                tableSettings: IDataTableSettings | null,
            ) =>
            {
                if (!tableSettings) return <div>Loading...</div>;

                const hiddenFieldsSet = new Set(
                    Object.keys(tableSettings.hiddenFields).filter((key) => tableSettings.hiddenFields[key]),
                );
                const visibleFields = tableFields.filter((f) => !hiddenFieldsSet.has(f.id));
                const sortedFields = visibleFields.sort((a, b) =>
                {
                    const orderA = tableSettings.order.indexOf(a.id);
                    const orderB = tableSettings.order.indexOf(b.id);
                    return orderA - orderB;
                });

                return (
                    <Box sx={{ width: '100%' }}>
                        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <TableContainer>
                                <Table>
                                    <TableHead
                                        sx={{
                                            bgcolor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? alpha(theme.palette.primary.light, 0.15)
                                                    : alpha(theme.palette.primary.main, 0.12),
                                        }}
                                    >
                                        <TableRow>
                                            {sortedFields.map((field) => (
                                                <TableCell
                                                    key={field.id}
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: (theme) =>
                                                            theme.palette.mode === 'dark'
                                                                ? theme.palette.primary.light
                                                                : theme.palette.primary.dark,
                                                    }}
                                                >
                                                    {field.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {innerEntries.map((member) => (
                                            <TableRow
                                                hover
                                                key={member.id}
                                                onDoubleClick={() =>
                                                    navigate(`/${ELinks.PEOPLE_EMPLOYEES}/${member.id}`)
                                                }
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {sortedFields.map((field) => (
                                                    <TableCell key={field.id}>
                                                        {field.id === 'name' && (
                                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                                <EmployeePicture
                                                                    employee={{
                                                                        id: member.id,
                                                                        firstName: member.firstName,
                                                                        lastName: member.lastName,
                                                                        pictureId: member.pictureId,
                                                                    }}
                                                                    avatarSize={32}
                                                                />
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight={500}>
                                                                        <EmployeeOverlay employeeId={member.id}>
                                                                            {member.name}
                                                                        </EmployeeOverlay>
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        {member.role}
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                        )}
                                                        {field.id === 'status' && (
                                                            <Chip
                                                                label={member.status}
                                                                size="small"
                                                                color={member.statusColor}
                                                            />
                                                        )}
                                                        {field.id === 'availability' && (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    minWidth: 120,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        height: 4,
                                                                        flex: 1,
                                                                        borderRadius: 2,
                                                                        backgroundColor: alpha(
                                                                            theme.palette.divider,
                                                                            0.2,
                                                                        ),
                                                                        overflow: 'hidden',
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            height: '100%',
                                                                            width: `${member.availability}%`,
                                                                            background: getAvailabilityGradient(
                                                                                member.availability,
                                                                            ),
                                                                            borderRadius: 2,
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: getAvailabilityColor(
                                                                            member.availability,
                                                                        ),
                                                                        fontWeight: 600,
                                                                        minWidth: 35,
                                                                    }}
                                                                >
                                                                    {member.availability}%
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {field.id === 'skills' && (
                                                            <Stack direction="row" spacing={0.5}>
                                                                {member.skills
                                                                    .slice(0, 2)
                                                                    .map((skill, skillIndex: number) => (
                                                                        <Chip
                                                                            key={skillIndex}
                                                                            label={skill.title || skill.name || 'Skill'}
                                                                            size="small"
                                                                            variant="outlined"
                                                                        />
                                                                    ))}
                                                                {member.skills.length > 2 && (
                                                                    <Chip
                                                                        label={`+${member.skills.length - 2}`}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ opacity: 0.7 }}
                                                                    />
                                                                )}
                                                            </Stack>
                                                        )}
                                                        {field.id === 'actions' && (
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) =>
                                                                    {
                                                                        e.preventDefault();
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        navigate(
                                                                            `/${ELinks.PEOPLE_EMPLOYEES}/${member.id}`,
                                                                        );
                                                                    }}
                                                                >
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                                {featureFlags.messages && (
                                                                    <>
                                                                        <IconButton size="small">
                                                                            <CompareIcon fontSize="small" />
                                                                        </IconButton>
                                                                        <IconButton size="small">
                                                                            <EmailIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </>
                                                                )}
                                                            </Stack>
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                );
            },
        },
        {
            id: 'list' as any,
            title: 'Liste',
            icon: <TableRows fontSize="small" />,
            minWidth: 0,
            renderContainer: (allEntities: TeamMember[], innerEntries: TeamMember[]) => (
                <Container>
                    {innerEntries.map((member: TeamMember) => (
                        <Box
                            key={member.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 2,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:hover': { bgcolor: 'action.hover' },
                                cursor: 'pointer',
                            }}
                            onClick={() => navigate(`/${ELinks.PEOPLE_EMPLOYEES}/${member.id}`)}
                        >
                            <EmployeePicture
                                employee={{
                                    id: member.id,
                                    firstName: member.firstName,
                                    lastName: member.lastName,
                                    pictureId: member.pictureId,
                                }}
                                avatarSize={40}
                            />
                            <Box sx={{ ml: 2, flex: 1 }}>
                                <Typography variant="body1" fontWeight={500}>
                                    {member.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {member.role} • {member.availability}% verfügbar
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: getAvailabilityColor(member.availability),
                                }}
                            />
                        </Box>
                    ))}
                </Container>
            ),
        },
    ];

    const actions: IEntitiesPageAction[] = [];

    const tableFields: IDataTableField<any>[] = [
        { id: 'name', label: 'Mitarbeiter', align: 'left', disablePadding: false, type: 'string' },
        { id: 'status', label: 'Status', align: 'left', disablePadding: false, type: 'string' },
        { id: 'availability', label: 'Verfügbarkeit', align: 'left', disablePadding: false, type: 'string' },
        { id: 'skills', label: 'Skills', align: 'left', disablePadding: false, type: 'string' },
        { id: 'actions', label: 'Aktionen', align: 'left', disablePadding: false, type: 'string' },
    ];

    const roles: IMvpRole[] = [
        {
            id: 'manager',
            label: 'Manager',
            icon: getAppIcon(EIconCategory.TEAMS),
        },
        {
            id: 'admin',
            label: 'Admin',
            icon: getAppIcon(EIconCategory.SETTINGS),
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'employees',
            label: 'Mitarbeitende',
            icon: getAppIcon(EIconCategory.EMPLOYEES),
            onClick: () => navigate(`/${ELinks.PEOPLE_EMPLOYEES}`),
            variant: 'primary',
        },
        {
            id: 'org-units',
            label: 'Abteilungen',
            icon: getAppIcon(EIconCategory.DEPARTMENTS),
            onClick: () => navigate(`/${ELinks.PEOPLE_ORGUNITS}`),
        },
    ];

    return (
        <>
            <MvpSubNav
                currentPageTitle="Mein Team"
                roles={roles}
                activeRole={activeRole}
                onRoleChange={setActiveRole}
                quickActions={quickActions}
            />
            {teamMembers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        Keine Team-Mitglieder gefunden
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Sie sind noch nicht als Leiter einer Organisationseinheit eingetragen.
                    </Typography>
                </Box>
            ) : (
                <EntitiesPage
                    title="My Team"
                    subTitle="Verwalten Sie Ihre Team-Mitglieder, verfolgen Sie die Verfügbarkeit und planen Sie wichtige Gespräche."
                    entities={teamMembers}
                    selected={selected}
                    loadingError={null}
                    tableFields={tableFields}
                    tableId="my-team-table"
                    setSelected={setSelected}
                    filter={(
                        entity: TeamMember,
                        selectedView: { filter: (entity: TeamMember) => boolean },
                        searchText: string,
                    ) => selectedView.filter(entity) && textContainsLowerCase(searchText, entity.name)}
                    views={[
                        {
                            id: 'all',
                            title: 'Alle Team-Mitglieder',
                            filter()
                            {
                                return true;
                            },
                        },
                        {
                            id: 'available',
                            title: 'Verfügbar (≥60%)',
                            filter(entity: TeamMember)
                            {
                                return entity.availability >= 60;
                            },
                        },
                        {
                            id: 'partial',
                            title: 'Teilweise verfügbar',
                            filter(entity: TeamMember)
                            {
                                return entity.availability >= 30 && entity.availability < 60;
                            },
                        },
                        {
                            id: 'busy',
                            title: 'Ausgelastet (<30%)',
                            filter(entity: TeamMember)
                            {
                                return entity.availability < 30;
                            },
                        },
                    ]}
                    types={types}
                    actions={actions}
                    chips={renderTeamStatsChips()}
                />
            )}
        </>
    );
}
